import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Repeat, SkipForward, X, RotateCw, History, PauseCircle, PlayCircle } from "lucide-react";

const FREQUENCY_OPTIONS = [
  { key: "one_time",      label: "One Time" },
  { key: "weekly",        label: "Weekly" },
  { key: "biweekly",      label: "Every 2 Weeks" },
  { key: "every_3_weeks", label: "Every 3 Weeks" },
  { key: "every_4_weeks", label: "Every 4 Weeks" },
  { key: "monthly",       label: "Monthly" },
  { key: "custom",        label: "Custom Schedule" },
];

const FREQ_LABEL = (k) => FREQUENCY_OPTIONS.find((f) => f.key === k)?.label || k;

const NEXT_VISIT_PREVIEW_DAYS = {
  weekly: 7, biweekly: 14, every_3_weeks: 21, every_4_weeks: 28,
  monthly: 30, every_6_weeks: 42, one_time: 0, custom: null,
};

function addDaysIso(iso, days) {
  if (!iso || !days) return null;
  try {
    const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + Number(days));
    return d.toISOString().slice(0, 10);
  } catch { return null; }
}

// iter149 — Customer-portal panel for recurring service self-service.
// Rewrite of iter104 that replaces every `window.confirm()` (silently
// blocked on mobile Safari / PWA / in-app browsers) with a proper
// AlertDialog so the "Cancel All Future Services" + "Change Frequency"
// buttons work on every device. Actions still map 1:1 to backend
// endpoints: /me/recurring/change-frequency, /me/recurring/skip-next,
// /me/recurring/cancel, /me/recurring/audit.
export default function ManageSchedulePanel({ user, onRefresh }) {
  const rs = user?.recurring_schedule;
  const [busy, setBusy] = useState(null);
  const [audit, setAudit] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);

  // Confirmation dialog state — { kind: "cancel"|"skip"|"freq", freq?: string }
  const [confirmState, setConfirmState] = useState(null);

  const loadAudit = async () => {
    try {
      const { data } = await api.get("/me/recurring/audit?limit=25");
      setAudit(data.audit || []);
      setAuditOpen(true);
    } catch { toast.error("Could not load change history"); }
  };

  useEffect(() => { if (!auditOpen) setAudit([]); }, [auditOpen]);

  if (!rs) return null;

  const isActive = (rs.status || "active") === "active";

  const doChangeFreq = async (newFreq) => {
    setBusy("freq");
    try {
      await api.post("/me/recurring/change-frequency", {
        service_frequency: newFreq,
        service_key: rs.service_key,
        square_feet: rs.square_feet || 4000,
        payment_method: rs.payment_method || "stripe",
        preferred_service_day: rs.preferred_service_day || "wednesday",
        preferred_time_window: rs.preferred_time_window || "morning",
      });
      toast.success(`Schedule updated to ${FREQ_LABEL(newFreq)}. Future visits regenerated.`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update schedule");
    } finally {
      setBusy(null);
      setConfirmState(null);
    }
  };

  const doSkipNext = async () => {
    setBusy("skip");
    try {
      const r = await api.post("/me/recurring/skip-next");
      toast.success(`Skipped visit on ${r.data.skipped_date}`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not skip next visit");
    } finally {
      setBusy(null);
      setConfirmState(null);
    }
  };

  // iter250 — Pause / Resume Service actions
  const doPause = async (resumeDate) => {
    setBusy("pause");
    try {
      await api.post("/me/recurring/pause", { resume_date: resumeDate || null });
      toast.success(
        resumeDate
          ? `Service paused. It will automatically resume on ${resumeDate}.`
          : "Service paused. Call or use Resume Now when you're ready.",
      );
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pause service");
    } finally {
      setBusy(null);
      setConfirmState(null);
    }
  };

  const doResume = async () => {
    setBusy("resume");
    try {
      await api.post("/me/recurring/resume");
      toast.success("Service resumed. Future visits are being scheduled again.");
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume service");
    } finally {
      setBusy(null);
      setConfirmState(null);
    }
  };

  const doCancelAll = async () => {
    setBusy("cancel");
    try {
      const r = await api.post("/me/recurring/cancel");
      toast.success(`${r.data.future_cancelled || 0} future visits cancelled.`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel recurring service");
    } finally {
      setBusy(null);
      setConfirmState(null);
    }
  };

  // Preview next visit date for the frequency-change confirmation dialog
  // so the customer sees the exact next scheduled service date before
  // committing (per the spec: "Show the next scheduled service date
  // before saving. Ask for confirmation.").
  const previewNextVisit = (newFreq) => {
    const days = NEXT_VISIT_PREVIEW_DAYS[newFreq];
    if (days === null) return "Custom (set on the next screen)";
    if (days === 0) return "One-time visit only — no future recurrence";
    const anchor = rs.last_service_date || rs.next_service_date || (new Date()).toISOString().slice(0, 10);
    return addDaysIso(anchor, days) || "—";
  };

  const openConfirm = (state) => setConfirmState(state);
  const closeConfirm = () => { if (!busy) setConfirmState(null); };

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6" data-testid="manage-schedule-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Repeat className="w-4 h-4 text-[#047857]" />
        <div className="text-[10px] uppercase tracking-[0.16em] font-extrabold text-gray-500">Recurring Schedule</div>
        <span className={`text-[9px] uppercase tracking-[0.14em] font-extrabold px-2 py-0.5 rounded-full border ${
          isActive ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : "bg-gray-100 text-gray-600 border-gray-200"
        }`}>{isActive ? "Active" : rs.status}</span>
      </div>
      <div className="mt-2 text-[15px] font-extrabold text-gray-900 tracking-tight">
        {rs.frequency_label || rs.frequency} · {rs.service_key?.replace(/_/g, " ")}
      </div>
      {isActive ? (
        <div className="text-[12px] text-gray-500" data-testid="next-service-line">
          Next service: <span className="font-bold text-gray-800" data-testid="next-service-date">{rs.next_service_date || "—"}</span>
        </div>
      ) : (
        <div className="text-[12px] text-rose-800 font-bold" data-testid="recurring-cancelled-line">
          Recurring Service Cancelled
        </div>
      )}

      {isActive && (
        <>
          {/* Frequency select */}
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500 mb-1.5">Change schedule</div>
            <div className="flex flex-wrap gap-1.5">
              {FREQUENCY_OPTIONS.map((f) => {
                const isCurrent = rs.frequency === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => {
                      if (isCurrent || busy !== null) return;
                      openConfirm({ kind: "freq", freq: f.key });
                    }}
                    disabled={busy !== null}
                    data-testid={`freq-${f.key}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-extrabold border transition disabled:opacity-60 ${
                      isCurrent
                        ? "bg-[#047857] text-white border-[#047857]"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 active:scale-[0.97]"
                    }`}
                  >
                    {busy === "freq" && confirmState?.freq === f.key
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-5 flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => openConfirm({ kind: "skip" })} disabled={busy !== null}
                    className="rounded-full font-extrabold" data-testid="skip-next-btn">
              {busy === "skip" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <SkipForward className="w-4 h-4 mr-1.5" />}
              Skip Next Visit
            </Button>
            <Button variant="outline"
                    onClick={() => openConfirm({ kind: "pause", resumeDate: "" })}
                    disabled={busy !== null}
                    className="rounded-full font-extrabold text-amber-800 border-amber-300 hover:bg-amber-50"
                    data-testid="pause-service-btn">
              {busy === "pause" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <PauseCircle className="w-4 h-4 mr-1.5" />}
              Pause Service
            </Button>
            <Button variant="outline" onClick={loadAudit}
                    className="rounded-full font-extrabold" data-testid="audit-btn">
              <History className="w-4 h-4 mr-1.5" /> Change History
            </Button>
            <Button variant="outline" onClick={() => openConfirm({ kind: "cancel" })} disabled={busy !== null}
                    className="rounded-full font-extrabold text-rose-800 border-rose-300 hover:bg-rose-50"
                    data-testid="cancel-all-btn">
              {busy === "cancel" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <X className="w-4 h-4 mr-1.5" />}
              Cancel Service
            </Button>
          </div>

          <p className="mt-3 text-[11px] text-gray-500 leading-relaxed">
            <b>Skip</b> = miss one visit only.
            &nbsp;<b>Pause</b> = temporarily stop and auto-resume on a date you choose.
            &nbsp;<b>Change Frequency</b> = get service more or less often.
            &nbsp;<b>Cancel</b> = stop recurring service completely.
            <br/>Your saved payment method and account history stay intact for all four options.
            <a href="tel:9048328730" className="text-[#047857] font-bold"> Call (904) 832-8730</a> anytime.
          </p>
        </>
      )}

      {!isActive && rs.status === "paused" && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
          <div className="flex items-center gap-1.5 font-extrabold mb-1">
            <PauseCircle className="w-3.5 h-3.5" /> Recurring service is paused
          </div>
          <div className="mb-2">
            {rs.resume_at
              ? <>Auto-resumes on <b>{rs.resume_at}</b>.</>
              : <>Paused indefinitely. Resume anytime.</>}
          </div>
          <Button size="sm" onClick={doResume} disabled={busy !== null}
                  className="rounded-full font-extrabold bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="resume-service-btn">
            {busy === "resume" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <PlayCircle className="w-4 h-4 mr-1.5" />}
            Resume Now
          </Button>
        </div>
      )}

      {!isActive && rs.status !== "paused" && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3 text-[12px] text-gray-600">
          <div className="flex items-center gap-1.5 font-extrabold text-gray-800 mb-1">
            <RotateCw className="w-3.5 h-3.5" /> Recurring service is cancelled
          </div>
          Your history is safe. To reactivate at any time, call
          <a href="tel:9048328730" className="text-[#047857] font-bold"> (904) 832-8730 </a>
          — we&apos;ll reuse your saved payment method.
        </div>
      )}

      {auditOpen && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">Change history</div>
            <button onClick={() => setAuditOpen(false)} className="text-gray-400 hover:text-gray-700"><X className="w-3.5 h-3.5" /></button>
          </div>
          {audit.length === 0 ? (
            <div className="text-[12px] text-gray-500 mt-1" data-testid="audit-empty">No schedule changes yet.</div>
          ) : (
            <ul className="mt-2 divide-y divide-gray-200 max-h-52 overflow-y-auto" data-testid="audit-list">
              {audit.map((row) => (
                <li key={row.log_id} className="py-1.5 flex items-start justify-between gap-2 text-[11px]">
                  <div className="min-w-0">
                    <div className="font-extrabold text-gray-800">{row.action.replace(/_/g, " ")}</div>
                    <div className="text-gray-500">by {row.actor} · {row.actor_kind}</div>
                  </div>
                  <div className="text-gray-500 shrink-0 tabular-nums">{(row.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* iter149 — Accessible confirmation dialog. Replaces window.confirm
          which was silently blocked on mobile Safari / PWA / in-app
          browsers, making Cancel + Frequency buttons appear to do nothing. */}
      <AlertDialog open={!!confirmState} onOpenChange={(o) => { if (!o) closeConfirm(); }}>
        <AlertDialogContent data-testid="schedule-confirm-dialog">
          {confirmState?.kind === "cancel" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel all future services?</AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] leading-relaxed">
                  Every scheduled future visit will be cancelled. Your completed
                  visits, invoices, payment history, and saved card stay untouched
                  — you can reactivate anytime.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="schedule-confirm-no" disabled={busy !== null}>Keep my subscription</AlertDialogCancel>
                <AlertDialogAction onClick={doCancelAll} disabled={busy !== null}
                                   data-testid="schedule-confirm-yes"
                                   className="bg-rose-700 hover:bg-rose-800">
                  {busy === "cancel" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Yes, cancel future visits
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {confirmState?.kind === "skip" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Skip your next visit?</AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] leading-relaxed">
                  We&apos;ll skip the next scheduled visit on
                  <span className="font-extrabold text-gray-800"> {rs.next_service_date || "your calendar"}</span>.
                  The following visit will happen as normal — nothing else changes.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="schedule-confirm-no" disabled={busy !== null}>Keep it scheduled</AlertDialogCancel>
                <AlertDialogAction onClick={doSkipNext} disabled={busy !== null}
                                   data-testid="schedule-confirm-yes">
                  {busy === "skip" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Yes, skip this visit
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {confirmState?.kind === "freq" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Change to {FREQ_LABEL(confirmState.freq)}?</AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] leading-relaxed">
                  Your next scheduled visit will move to
                  <span className="font-extrabold text-gray-800"> {previewNextVisit(confirmState.freq)}</span>.
                  Only future visits change — completed visits and paid
                  invoices stay exactly as they are, and your subscription price
                  stays locked at
                  <span className="font-extrabold text-gray-800"> ${Number(rs.locked_price || rs.base_price || 0).toFixed(2)}</span> per visit.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="schedule-confirm-no" disabled={busy !== null}>Keep current</AlertDialogCancel>
                <AlertDialogAction onClick={() => doChangeFreq(confirmState.freq)} disabled={busy !== null}
                                   data-testid="schedule-confirm-yes">
                  {busy === "freq" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Yes, update schedule
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {confirmState?.kind === "pause" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Pause your recurring service?</AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] leading-relaxed">
                  We&apos;ll pause your recurring visits and keep your account,
                  saved payment method, and history intact. Pick a date to
                  automatically resume — or leave it blank and use
                  <b> Resume Now</b> whenever you&apos;re ready.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="my-3">
                <label className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Auto-resume date (optional)</label>
                <input type="date"
                        min={new Date(Date.now() + 86400000).toISOString().slice(0,10)}
                        value={confirmState.resumeDate || ""}
                        onChange={(e) => setConfirmState((s) => ({ ...s, resumeDate: e.target.value }))}
                        className="mt-1 w-full h-11 px-3 rounded-lg border border-gray-300 text-sm font-semibold text-gray-900 bg-white"
                        data-testid="pause-resume-date" />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="schedule-confirm-no" disabled={busy !== null}>Not now</AlertDialogCancel>
                <AlertDialogAction onClick={() => doPause(confirmState.resumeDate)} disabled={busy !== null}
                                   data-testid="schedule-confirm-yes"
                                   className="bg-amber-700 hover:bg-amber-800">
                  {busy === "pause" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Yes, pause service
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
